// Public "delete my account" page. Google Play requires a web link where a user can ask
// for deletion without installing the app.
//
// Flow: email -> one-time code -> type DELETE -> the `delete-account` edge function (which
// deletes the caller named by their JWT) -> local sign-out.
//
// This page NEVER signs in anonymously. It does not use useAnonSession, and nothing mounted
// globally in App.tsx does either (only ScorePage does). The only session it ever holds is
// the real one verifyOtp mints, and that is dropped as soon as deletion finishes.
//
// The "code sent" message is deliberately the same whether or not an account exists:
// shouldCreateUser is false, so an unknown address either errors or silently succeeds, and
// this page must not become a way to probe who has an account.

import { useState } from 'react';
import { supabase } from '../lib/supabase';

type Step = 'email' | 'code' | 'confirm' | 'done';
type ErrLike = { message?: string; status?: number; name?: string } | null;

function isRateLimit(e: ErrLike): boolean {
  const m = (e?.message ?? '').toLowerCase();
  return e?.status === 429 || m.includes('rate limit') || m.includes('too many') || m.includes('security purposes');
}

function isNetwork(e: ErrLike): boolean {
  const m = (e?.message ?? '').toLowerCase();
  return (
    m.includes('failed to fetch') ||
    m.includes('network') ||
    m.includes('load failed') ||
    e?.name === 'FunctionsFetchError' ||
    e?.name === 'AuthRetryableFetchError'
  );
}

function friendly(err: unknown, what: 'send' | 'verify' | 'delete'): string {
  const e = err as ErrLike;
  if (isRateLimit(e)) return 'Too many attempts. Wait a minute or two and try again.';
  if (isNetwork(e)) return 'Could not reach the server. Check your connection and try again.';
  if (what === 'verify') return 'That code is wrong or has expired. Check it, or go back and request a new one.';
  if (what === 'delete') {
    return 'We could not delete the account. Nothing was changed. Try again, or email support@twobeersstudios.com.';
  }
  return 'Something went wrong sending the code. Try again in a moment.';
}

export default function DeleteAccountPage() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cleanEmail = email.trim();
  const emailOk = /^\S+@\S+\.\S+$/.test(cleanEmail);
  const cleanCode = code.replace(/\s+/g, '');
  const codeOk = /^\d{6,10}$/.test(cleanCode);

  async function sendCode(e: React.FormEvent) {
    e.preventDefault();
    if (!emailOk || busy) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: cleanEmail,
      options: { shouldCreateUser: false },
    });
    setBusy(false);
    // An unknown address may come back as an error. Treat that exactly like success so the
    // page never says whether an account exists. Only rate limits and network failures are
    // worth surfacing, and neither reveals anything about the address.
    if (err && (isRateLimit(err as ErrLike) || isNetwork(err as ErrLike))) {
      setError(friendly(err, 'send'));
      return;
    }
    setNotice('If an account exists for that address, we sent a code. It can take a minute to arrive.');
    setStep('code');
  }

  async function checkCode(e: React.FormEvent) {
    e.preventDefault();
    if (!codeOk || busy) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.auth.verifyOtp({ email: cleanEmail, token: cleanCode, type: 'email' });
    setBusy(false);
    if (err) {
      setError(friendly(err, 'verify'));
      return;
    }
    setNotice(null);
    setStep('confirm');
  }

  async function deleteIt(e: React.FormEvent) {
    e.preventDefault();
    if (typed !== 'DELETE' || busy) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.functions.invoke('delete-account', { method: 'POST' });
    if (err) {
      setBusy(false);
      setError(friendly(err, 'delete'));
      return;
    }
    // The user row is gone, so the token is dead; this just clears it from the browser.
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      /* nothing left to sign out of */
    }
    setBusy(false);
    setStep('done');
  }

  function startOver() {
    setStep('email');
    setCode('');
    setTyped('');
    setNotice(null);
    setError(null);
  }

  return (
    <div className="wrap" style={{ maxWidth: 640 }}>
      <h1>Delete your 4sum account</h1>
      <p className="muted" style={{ marginTop: 0 }}>
        4sum, by Two Beers Studios
      </p>

      {step !== 'done' && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3>What gets deleted</h3>
          <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
            <li>Your account and email address</li>
            <li>Your profile (name and handle)</li>
            <li>Your Pro entitlement record</li>
            <li>Any cloud backups</li>
            <li>Group-round memberships, and group rounds you host</li>
            <li>Shared course-GPS contributions you made</li>
          </ul>
          <h3>What stays</h3>
          <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
            <li>Rounds stored only on your phone. Delete the app to remove them.</li>
            <li>Tournament seats stay on the course scorecard, with your account unlinked.</li>
          </ul>
          <p style={{ margin: '0 0 8px' }}>
            <strong>Subscriptions are not cancelled by this.</strong> Cancel in the App Store or Google Play
            yourself, or billing continues.
          </p>
          <p style={{ margin: 0 }}>
            Deletion is immediate and permanent. Backups held by our database provider roll off within 7 days.
          </p>
        </div>
      )}

      {step === 'email' && (
        <form className="card" onSubmit={sendCode}>
          <div className="field">
            <label htmlFor="da-email">Email on your 4sum account</label>
            <input
              id="da-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
          </div>
          {error && (
            <p className="err" role="alert">
              {error}
            </p>
          )}
          <button type="submit" disabled={!emailOk || busy}>
            Send me a code
          </button>
        </form>
      )}

      {step === 'code' && (
        <form className="card" onSubmit={checkCode}>
          {notice && <p style={{ marginTop: 0 }}>{notice}</p>}
          <div className="field">
            <label htmlFor="da-code">Code from the email</label>
            <input
              id="da-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={12}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={busy}
            />
          </div>
          {error && (
            <p className="err" role="alert">
              {error}
            </p>
          )}
          <div className="row">
            <button type="submit" disabled={!codeOk || busy}>
              Verify code
            </button>
            <button type="button" className="secondary" onClick={startOver} disabled={busy}>
              Use a different email
            </button>
          </div>
        </form>
      )}

      {step === 'confirm' && (
        <form className="card" onSubmit={deleteIt}>
          <p style={{ marginTop: 0 }}>
            Verified. This will permanently delete the 4sum account for <strong>{cleanEmail}</strong>.
          </p>
          <div className="field">
            <label htmlFor="da-confirm">Type DELETE to confirm</label>
            <input
              id="da-confirm"
              autoComplete="off"
              autoCapitalize="characters"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={busy}
            />
          </div>
          {error && (
            <p className="err" role="alert">
              {error}
            </p>
          )}
          <button type="submit" className="danger" disabled={typed !== 'DELETE' || busy}>
            Delete my account
          </button>
        </form>
      )}

      {step === 'done' && (
        <div className="card" role="status">
          <h3>Your account has been deleted</h3>
          <p style={{ margin: 0 }}>
            Your 4sum account and its cloud data are gone. If you subscribed to Pro, remember to cancel in the
            App Store or Google Play. You can delete the app from your phone to remove local rounds.
          </p>
        </div>
      )}

      {step !== 'done' && (
        <p className="muted" style={{ marginTop: 16 }}>
          Can&apos;t get the code? Email <a href="mailto:support@twobeersstudios.com">support@twobeersstudios.com</a>{' '}
          from the account&apos;s address.
        </p>
      )}
    </div>
  );
}
